/*
 * ce-ring-sizer.js — on-screen ring sizer (snippets/ce-ring-sizer.liquid).
 * WHY JS: the circle must be drawn at its REAL physical size. CSS millimetres are not physical on phones (1 CSS px is
 * 1/96 in by definition, a phone shows ~150 CSS px per inch), so we need CSS px per mm for THIS screen:
 *   1. known Apple devices → screen points × devicePixelRatio → ppi table (confirmed by the shopper);
 *   2. known Android devices → model from the user-agent client hints → assets/ce-sizer-devices.json (fetched only then);
 *   3. anything else → bank-card check (ISO/IEC 7810 ID-1: 53.98 × 85.60 mm). The dot the shopper slides sits exactly
 *      above the right edge of the card outline: "slide the dot to the edge of your card" — no abstract slider.
 *   4. remembered per screen signature in localStorage. Pinch-zoom is detected (visualViewport.scale) and flagged.
 * Sizes are read from the product's own size buttons (label lines "US 7 · UK O · EU 54 · JP 14");
 * inner diameter of a US size = 11.63 + 0.8128 × size (mm). Desktop (fine pointer, no touch) is steered to the phone.
 */
(() => {
  const CARD_SHORT = 53.98, CARD_LONG = 85.6;                  // mm, ISO ID-1
  const D_MIN = 13.5, D_MAX = 24.5;                            // circle range, mm (US 2.3 → US 15.8)
  const INSET = 10, EDGE_GAP = 6, PXMM_MIN = 2.6;              // card check geometry (CSS px): left guide line, free space at the right, smallest outline
  const usToMm = (us) => 11.63 + 0.8128 * us;
  const mmToUs = (mm) => (mm - 11.63) / 0.8128;
  const sig = () => `${Math.min(screen.width, screen.height)}x${Math.max(screen.width, screen.height)}@${Math.round(devicePixelRatio * 100) / 100}`;

  // Apple devices by logical screen (portrait) and scale → ppi (physical px / in), the name shown, the same-screen siblings.
  const APPLE = [
    ['440x956', 3, 460, 'iPhone 16 Pro Max', '17 Pro Max'], ['402x874', 3, 460, 'iPhone 16 Pro', '17, 17 Pro'], ['420x912', 3, 460, 'iPhone Air', ''],
    ['393x852', 3, 460, 'iPhone 15', '14 Pro, 15 Pro, 16'], ['430x932', 3, 460, 'iPhone 15 Pro Max', '14 Pro Max, 15 Plus, 16 Plus'],
    ['428x926', 3, 458, 'iPhone 13 Pro Max', '12 Pro Max, 14 Plus'], ['390x844', 3, 460, 'iPhone 14', '12, 13, 16e'],
    ['375x812', 3, 458, 'iPhone 11 Pro', 'X, XS'], ['414x896', 3, 458, 'iPhone 11 Pro Max', 'XS Max'], ['414x896', 2, 326, 'iPhone 11', 'XR'],
    ['414x736', 3, 401 / 2.608 * 3, 'iPhone 8 Plus', '6 Plus, 7 Plus'], ['375x667', 2, 326, 'iPhone SE', '6, 7, 8'], ['320x568', 2, 326, 'iPhone SE (1st gen)', '5, 5s'],
    ['1032x1376', 2, 264, 'iPad Pro 13"', ''], ['1024x1366', 2, 264, 'iPad Pro 12.9"', 'Air 13"'], ['834x1210', 2, 264, 'iPad Pro 11"', ''],
    ['834x1194', 2, 264, 'iPad Air', 'Pro 11"'], ['820x1180', 2, 264, 'iPad', 'Air 11"'], ['810x1080', 2, 264, 'iPad (9th gen)', '7th, 8th gen'],
    ['768x1024', 2, 264, 'iPad', ''], ['744x1133', 2, 326, 'iPad mini', ''],
  ];
  const isApple = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const appleWidthMm = (row) => (Number(row[0].split('x')[0]) * row[1] * 25.4) / row[2]; // native points × scale ÷ ppi
  const detectApple = () => {
    if (!isApple) return null;
    const key = `${Math.min(screen.width, screen.height)}x${Math.max(screen.width, screen.height)}`;
    const hit = APPLE.find(([k, scale]) => k === key && Math.abs(scale - devicePixelRatio) < 0.2);
    if (!hit) return null;
    return { name: hit[3], pxPerMm: Math.min(screen.width, screen.height) / appleWidthMm(hit) };
  };
  // every iPhone by name for the manual picker: [name, native points width, scale, ppi] (physical width derives from them)
  const PICKER_APPLE = [
    ['iPhone 17 Pro Max', 440, 3, 460], ['iPhone 17 Pro', 402, 3, 460], ['iPhone 17', 402, 3, 460], ['iPhone Air', 420, 3, 460],
    ['iPhone 16 Pro Max', 440, 3, 460], ['iPhone 16 Pro', 402, 3, 460], ['iPhone 16 Plus', 430, 3, 460], ['iPhone 16', 393, 3, 460], ['iPhone 16e', 390, 3, 460],
    ['iPhone 15 Pro Max', 430, 3, 460], ['iPhone 15 Pro', 393, 3, 460], ['iPhone 15 Plus', 430, 3, 460], ['iPhone 15', 393, 3, 460],
    ['iPhone 14 Pro Max', 430, 3, 460], ['iPhone 14 Pro', 393, 3, 460], ['iPhone 14 Plus', 428, 3, 458], ['iPhone 14', 390, 3, 460],
    ['iPhone 13 Pro Max', 428, 3, 458], ['iPhone 13 Pro', 390, 3, 460], ['iPhone 13', 390, 3, 460], ['iPhone 13 mini', 375, 3, 476],
    ['iPhone 12 Pro Max', 428, 3, 458], ['iPhone 12 Pro', 390, 3, 460], ['iPhone 12', 390, 3, 460], ['iPhone 12 mini', 375, 3, 476],
    ['iPhone 11 Pro Max', 414, 3, 458], ['iPhone 11 Pro', 375, 3, 458], ['iPhone 11', 414, 2, 326],
    ['iPhone XS Max', 414, 3, 458], ['iPhone XS', 375, 3, 458], ['iPhone X', 375, 3, 458], ['iPhone XR', 414, 2, 326],
    ['iPhone SE (2020/2022)', 375, 2, 326], ['iPhone 8 Plus', 414, 3, 461], ['iPhone 8', 375, 2, 326], ['iPhone 7 Plus', 414, 3, 461], ['iPhone 7', 375, 2, 326],
  ];
  // Android: rows {match: [model codes], name, px: [short, long], diag} from assets/ce-sizer-devices.json (Google Play model
  // codes = what Chromium's client hints report). CSS px per mm = screen CSS width / physical panel width, so a phone rendering
  // at FHD+ on a QHD+ panel (Samsung default) is still measured right. Foldables carry two rows: the panel is picked by aspect ratio.
  const matches = (model, key) => model.startsWith(key.toUpperCase()) && (model.length === key.length || !/[0-9]/.test(model.charAt(key.length)));
  const detectAndroid = (model, rows) => {
    if (!model || !rows || !rows.length) return null;
    const m = model.trim().toUpperCase();
    // longest matching code wins ("Pixel 9 Pro" must not fall on the "Pixel 9" row); equal length = a foldable's two panels
    const scored = rows.map((row) => ({ row, len: Math.max(0, ...(row.match || []).filter((key) => matches(m, key)).map((key) => key.length)) })).filter((s) => s.len > 0);
    if (!scored.length) return null;
    const best = Math.max(...scored.map((s) => s.len));
    const hits = scored.filter((s) => s.len === best).map((s) => s.row);
    const ratio = Math.max(screen.width, screen.height) / Math.min(screen.width, screen.height);
    const hit = hits.reduce((acc, row) => Math.abs(row.px[1] / row.px[0] - ratio) < Math.abs(acc.px[1] / acc.px[0] - ratio) ? row : acc, hits[0]);
    const ppi = Math.hypot(hit.px[0], hit.px[1]) / hit.diag;          // physical pixels per inch of the panel
    const widthMm = (hit.px[0] / ppi) * 25.4;                          // physical width of the panel
    return { name: hit.name, pxPerMm: Math.min(screen.width, screen.height) / widthMm };
  };
  const deviceName = async () => {
    try {
      if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
        const v = await navigator.userAgentData.getHighEntropyValues(['model']);
        if (v.model) return v.model;
      }
    } catch (error) { /* not available */ }
    const m = navigator.userAgent.match(/;\s*([^;)]+?)\s+Build\//);
    return m ? m[1] : '';
  };
  const mobileUA = /iPhone|iPod|Android|Mobile|IEMobile|Windows Phone/i.test(navigator.userAgent) || isApple;
  const isDesktop = () => !mobileUA && matchMedia('(pointer: fine)').matches && navigator.maxTouchPoints === 0;

  window.ceRingSizer = { detectApple, detectAndroid, usToMm, mmToUs, APPLE, appleWidthMm };
  document.querySelectorAll('modal-dialog.ce-sizer').forEach((modal) => {
    const t = (name) => modal.dataset[name] || '';
    const q = (s) => modal.querySelector(s);
    const steps = [...modal.querySelectorAll('.ce-sizer__step')];
    const back = q('[data-sizer-back]'), dots = q('.ce-sizer__dots');
    const sizeInputs = () => [...document.querySelectorAll('.ce-size__fieldset input[type="radio"]')];
    const history = [];
    let pxPerMm = 0, calibrated = false, diameter = 17.3;
    let devicesPromise = null;
    const loadDevices = () => devicesPromise || (devicesPromise = (t('devicesUrl') ? fetch(t('devicesUrl')).then((r) => (r.ok ? r.json() : [])) : Promise.resolve([])).then((d) => (Array.isArray(d) ? d : d.rows || [])).catch(() => []));

    // ---- catalogue of sizes from the product buttons (label lines carry every system)
    const currentSystem = () => document.documentElement.getAttribute('data-ce-size-system') || ((document.querySelector('.ce-size-label[data-ce-default]') || {}).dataset || {}).ceDefault || 'us';
    const sizes = () => sizeInputs().map((input) => {
      const label = document.querySelector(`label[for="${input.id}"]`);
      const line = (label && label.dataset.ceSizeValue) || input.value || '';
      const us = parseFloat((line.match(/US\s*([\d.]+)/) || [])[1] || line);
      const labels = {};
      line.split(' · ').forEach((part) => { const m = part.trim().match(/^([A-Z]{2})\s+(.+)$/); if (m) labels[m[1].toLowerCase()] = m[2]; });
      if (!Object.keys(labels).length) labels.us = line;
      return { input, us, mm: usToMm(us), labels };
    }).filter((s) => !isNaN(s.us)).sort((a, b) => a.us - b.us);
    const labelOf = (s) => s.labels[currentSystem()] || s.labels.us || String(s.us);
    // size for a measured inner diameter (mm): the largest size that is not bigger than the finger; the next size up only
    // when the measure is past 60% of the step to it (~0.49 mm) — between two sizes → the smaller one (sensor contact),
    // clamped to the range we sell. Used for the live label AND the result, so both always agree.
    const pick = (mm) => {
      const list = sizes(); if (!list.length) return null;
      let lower = null;
      list.forEach((s) => { if (s.mm <= mm + 0.001) lower = s; });
      if (!lower) return list[0];
      const upper = list[list.indexOf(lower) + 1];
      if (upper && mm - lower.mm > (upper.mm - lower.mm) * 0.6) return upper;
      return lower;
    };

    // ---- navigation
    const show = (name, push = true) => {
      if (push) history.push(name);
      steps.forEach((s) => { s.hidden = s.dataset.step !== name; });
      back.hidden = history.length <= 1;
      dots.hidden = !['intro', 'measure', 'result'].includes(name);
      [...dots.children].forEach((d, i) => d.classList.toggle('is-on', i === ['intro', 'measure', 'result'].indexOf(name)));
      if (name === 'calibrate') drawCard();
      if (name === 'picker') buildPicker();
      if (name === 'measure') { drawRing(); checkZoom(); }
      if (name === 'result') showResult();
      modal.querySelector('.ce-sizer__panel').scrollTop = 0;
    };
    back.addEventListener('click', () => { history.pop(); show(history[history.length - 1] || 'home', false); });

    // ---- what will happen for this phone: remembered → known Apple → known Android → card check
    const resolve = async () => {
      if (isDesktop()) return { step: 'desktop' };
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem('ce_sizer') || 'null'); } catch (error) { /* ignore */ }
      if (saved && saved.sig === sig() && saved.pxPerMm > 1) return { step: 'intro', pxPerMm: saved.pxPerMm };
      const apple = detectApple();
      if (apple) return { step: 'device', device: apple };
      const name = await deviceName();
      const android = detectAndroid(name, await loadDevices());
      if (android) return { step: 'device', device: android };
      return { step: 'picker', model: name }; // picking from the list beats any physical check; the card waits behind it
    };
    const start = async () => {
      const plan = await resolve();
      if (plan.step === 'intro') { pxPerMm = plan.pxPerMm; calibrated = true; }
      if (plan.device) {
        pxPerMm = plan.device.pxPerMm;
        q('[data-sizer-device-name]').textContent = plan.device.name;
      }
      if (pxPerMm < 1) pxPerMm = devicePixelRatio >= 2 ? 6 : 3.9; // starting guess for the card, if it is ever needed
      show(plan.step);
    };
    const remember = () => { try { localStorage.setItem('ce_sizer', JSON.stringify({ sig: sig(), pxPerMm })); } catch (error) { /* private mode */ } };

    // ---- sliders (pointer-driven; value 0..1; vertical: top = larger; horizontal: the thumb centre follows the track)
    const sliders = {};
    modal.querySelectorAll('[data-sizer-slider]').forEach((el) => {
      const track = el.querySelector('.ce-sizer__track'), thumb = el.querySelector('.ce-sizer__thumb');
      let value = 0.5, dragging = false;
      const horizontal = el.dataset.sizerAxis === 'x';
      const render = () => { if (horizontal) thumb.style.left = `${track.offsetLeft + value * track.offsetWidth}px`; else thumb.style.top = `${(1 - value) * 100}%`; el.setAttribute('aria-valuenow', Math.round(value * 100)); };
      const set = (v, fire = true) => { value = Math.min(1, Math.max(0, v)); render(); if (fire && sliders[el.dataset.sizerSlider].onChange) sliders[el.dataset.sizerSlider].onChange(value); };
      const fromEvent = (event) => { const r = track.getBoundingClientRect(); if (horizontal) { if (r.width < 10) return; set((event.clientX - r.left) / r.width); } else { if (r.height < 10) return; set(1 - (event.clientY - r.top) / r.height); } };
      el.addEventListener('pointerdown', (event) => { dragging = true; try { el.setPointerCapture(event.pointerId); } catch (error) { /* synthetic pointer */ } fromEvent(event); event.preventDefault(); });
      el.addEventListener('pointermove', (event) => { if (dragging) fromEvent(event); });
      el.addEventListener('pointerup', () => { dragging = false; });
      el.addEventListener('pointercancel', () => { dragging = false; });
      el.addEventListener('keydown', (event) => { const step = event.shiftKey ? 0.05 : 0.01; if (event.key === 'ArrowUp' || event.key === 'ArrowRight') { set(value + step); event.preventDefault(); } if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') { set(value - step); event.preventDefault(); } });
      sliders[el.dataset.sizerSlider] = { set, get: () => value, render, onChange: null };
      render();
    });

    // ---- manual picker: every phone we know, one native grouped <select> (the OS wheel is the interactive menu)
    const BRAND_ALIAS = { Galaxy: 'Samsung', Pixel: 'Google', iPhone: 'Apple', iPad: 'Apple', Xperia: 'Sony', Moto: 'Motorola', razr: 'Motorola', vivo: 'Vivo', realme: 'Realme' };
    // most-bought first, so almost everyone finds their brand at the top of the wheel
    const BRAND_ORDER = ['Apple', 'Samsung', 'Xiaomi', 'Redmi', 'POCO', 'Google', 'OnePlus', 'Oppo', 'Vivo', 'Realme', 'Honor', 'Motorola', 'Huawei', 'Nothing', 'Sony', 'Fairphone'];
    const pickerSelect = q('[data-sizer-picker]'), pickerGo = q('[data-sizer-picker-go]');
    let pickerBuilt = false;
    const buildPicker = async () => {
      if (pickerBuilt || !pickerSelect) return;
      pickerBuilt = true;
      const rows = await loadDevices();
      const groups = new Map();
      const put = (brand, label, value) => {
        if (!groups.has(brand)) groups.set(brand, []);
        groups.get(brand).push([label, value]);
      };
      PICKER_APPLE.forEach((row, index) => put('Apple', row[0], `p:${index}`));
      const appleSeen = new Set();
      APPLE.forEach((row, index) => {
        const name = row[3];
        if (!/^iPad/.test(name) || appleSeen.has(name)) return; // iPhones come from PICKER_APPLE; iPads from the table
        appleSeen.add(name);
        put('Apple', name, `i:${index}`);
      });
      rows.forEach((row, index) => {
        const first = String(row.name || '').split(' ')[0];
        put(BRAND_ALIAS[first] || first, row.name, `a:${index}`);
      });
      const rank = (brand) => { const i = BRAND_ORDER.indexOf(brand); return i === -1 ? BRAND_ORDER.length : i; };
      [...groups.keys()].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b)).forEach((brand) => {
        const optgroup = document.createElement('optgroup');
        optgroup.label = brand;
        groups.get(brand).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true })).forEach(([label, value]) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = label;
          optgroup.appendChild(option);
        });
        pickerSelect.appendChild(optgroup);
      });
    };
    if (pickerSelect) {
      pickerSelect.addEventListener('change', () => pickerGo.setAttribute('aria-disabled', String(!pickerSelect.value)));
      pickerGo.addEventListener('click', async () => {
        const value = pickerSelect.value;
        if (!value) { pickerSelect.focus(); return; }
        const [kind, indexText] = value.split(':');
        const index = Number(indexText);
        if (kind === 'p') {
          const row = PICKER_APPLE[index];
          pxPerMm = Math.min(screen.width, screen.height) / ((row[1] * row[2] * 25.4) / row[3]);
        } else if (kind === 'i') {
          pxPerMm = Math.min(screen.width, screen.height) / appleWidthMm(APPLE[index]);
        } else {
          const row = (await loadDevices())[index];
          if (!row) return;
          const ppi = Math.hypot(row.px[0], row.px[1]) / row.diag;
          pxPerMm = Math.min(screen.width, screen.height) / ((row.px[0] / ppi) * 25.4);
        }
        calibrated = true; remember(); show('intro');
      });
    }

    // ---- card check: the outline's left edge sits on the guide line (INSET); the dot above it IS its right edge, so the
    // slider track spans exactly the outline widths the screen can show (smallest … screen width − EDGE_GAP).
    const card = q('[data-sizer-card]'), stage = q('.ce-sizer__stage--card'), guide = q('[data-sizer-guide]');
    const calSlider = q('[data-sizer-slider="calibrate"]'), calTrack = calSlider.querySelector('.ce-sizer__track');
    const doneBtn = q('[data-sizer-calibrated]'), tooSmall = q('[data-sizer-too-small]'), screenEl = q('[data-sizer-screen]');
    const cal = { min: PXMM_MIN, max: 7, moved: false };
    const updateDone = () => { doneBtn.textContent = cal.moved ? t('done') : t('first'); doneBtn.classList.toggle('is-waiting', !cal.moved); doneBtn.setAttribute('aria-disabled', String(!cal.moved)); };
    const drawCard = () => {
      const width = stage.clientWidth || modal.clientWidth || 360;
      cal.max = Math.max(cal.min + 0.5, (width - INSET - EDGE_GAP) / CARD_SHORT);
      calTrack.style.left = `${INSET + CARD_SHORT * cal.min}px`;
      calTrack.style.right = `${EDGE_GAP}px`;
      const guess = Math.min(cal.max, Math.max(cal.min, pxPerMm || (devicePixelRatio >= 2 ? 6 : 3.9)));
      cal.moved = false; updateDone();
      sliders.calibrate.set((guess - cal.min) / (cal.max - cal.min), false);
      renderCard(sliders.calibrate.get());
    };
    const renderCard = (v) => {
      pxPerMm = cal.min + v * (cal.max - cal.min);
      const width = CARD_SHORT * pxPerMm;
      card.style.width = `${width}px`;
      card.style.height = `${CARD_LONG * pxPerMm}px`;
      if (guide) guide.style.left = `${INSET + width}px`;
      if (tooSmall) tooSmall.hidden = !(v > 0.995);                  // the dot is at the very edge: the card is wider than the screen
      if (screenEl) { screenEl.hidden = !cal.moved || v > 0.995; screenEl.textContent = t('screenText').replace('[size]', (Math.hypot(screen.width, screen.height) / pxPerMm / 25.4).toFixed(1)); }
    };
    sliders.calibrate.onChange = (v) => { if (!cal.moved) { cal.moved = true; updateDone(); modal.classList.add('ce-sizer--moved'); } renderCard(v); };
    doneBtn.addEventListener('click', () => {
      if (!cal.moved) { calSlider.classList.remove('is-nudge'); void calSlider.offsetWidth; calSlider.classList.add('is-nudge'); return; }
      calibrated = true; remember(); show('intro');
    });
    q('[data-sizer-confirm-device]').addEventListener('click', () => { calibrated = true; remember(); show('intro'); });
    window.addEventListener('resize', () => { if (!q('[data-step="calibrate"]').hidden) { sliders.calibrate.render(); renderCard(sliders.calibrate.get()); } });

    // ---- measure: circle diameter from the slider (linear in mm), label = nearest size in the shopper's system
    const ring = q('[data-sizer-ring]'), sizeEl = q('[data-sizer-size]'), mmEl = q('[data-sizer-mm]');
    const drawRing = () => {
      const v = (diameter - D_MIN) / (D_MAX - D_MIN);
      sliders.measure.set(v, false);
      renderRing();
    };
    const renderRing = () => {
      const px = diameter * pxPerMm;
      ring.style.width = `${px}px`; ring.style.height = `${px}px`;
      const s = pick(diameter);
      sizeEl.textContent = s ? labelOf(s) : Math.round(mmToUs(diameter) * 2) / 2;
      mmEl.textContent = `Ø ${diameter.toFixed(1)} mm`;
    };
    sliders.measure.onChange = (v) => { const d = Math.round((D_MIN + v * (D_MAX - D_MIN)) * 20) / 20; if (Number.isFinite(d)) { diameter = d; renderRing(); } };
    const zoomEl = q('[data-sizer-zoom]');
    const checkZoom = () => { zoomEl.hidden = !(window.visualViewport && Math.abs(window.visualViewport.scale - 1) > 0.02); };
    if (window.visualViewport) window.visualViewport.addEventListener('resize', checkZoom);

    // ---- result
    let chosen = null;
    const showResult = () => {
      // same rule as the live label (pick): between two sizes → the smaller one
      chosen = pick(diameter);
      q('[data-sizer-result]').textContent = chosen ? labelOf(chosen) : '—';
      q('[data-sizer-apply]').hidden = !chosen;
    };
    q('[data-sizer-apply]').addEventListener('click', () => {
      if (chosen && chosen.input && !chosen.input.checked) chosen.input.click();
      modal.hide();
    });

    // ---- desktop step: QR to this page + copy link
    const desktop = q('[data-step="desktop"]');
    if (desktop) {
      const url = location.origin + location.pathname + '#find-my-size';
      const img = desktop.querySelector('[data-sizer-qr]');
      if (img) img.src = `https://api.qrserver.com/v1/create-qr-code/?size=440x440&margin=0&data=${encodeURIComponent(url)}`;
      const copy = desktop.querySelector('[data-sizer-copy]');
      if (copy) copy.addEventListener('click', async () => { try { await navigator.clipboard.writeText(url); copy.textContent = copy.dataset.done; } catch (error) { prompt('', url); } });
    }

    // ---- wiring
    modal.querySelectorAll('[data-sizer-go]').forEach((b) => b.addEventListener('click', () => {
      const to = b.dataset.sizerGo;
      if (to === 'measure-start') { start(); return; }
      if (to === 'measure' && !calibrated) { start(); return; }
      show(to);
    }));
    modal.querySelectorAll('[data-sizer-close]').forEach((b) => b.addEventListener('click', () => modal.hide()));
    const opener = document.querySelector(`modal-opener[data-modal="#${modal.id}"] button`);
    const cardNote = q('[data-sizer-card-note]');
    const reset = () => {
      history.length = 0; show('home');
      // the intro illustration is lazy (hidden step): start fetching it as soon as the tool opens, so the intro shows it at once
      const intro = q('.ce-sizer__intro-img');
      if (intro && intro.loading === 'lazy') intro.loading = 'eager';
      // tell the shopper up front when a bank card will be needed (only then)
      if (cardNote) { cardNote.hidden = true; resolve().then((plan) => { cardNote.hidden = plan.step !== 'calibrate'; }); }
    };
    if (opener) opener.addEventListener('click', reset);
    if (location.hash === '#find-my-size' && opener) setTimeout(() => opener.click(), 600);
    reset();
  });
})();
